import api from './api';
import type {
  ApiResponse,
  PaginatedResponse,
  Appointment,
  RecallType,
  PatientRecall,
} from '@/types';

/**
 * Phase 79 (RECALL-01..04) + 85-02 — the appointment-workflow API module
 * (day-of check-in, always on; recall engine behind
 * `feature:recalls_enabled`, 404 when off):
 *
 *   PATCH  /appointments/{id}/checkin  {step}              (appointments.edit)
 *   GET    /appointment-categories                        (NOT flag-gated — validation data)
 *   GET    /recall-types                                   (recalls.view)
 *   POST   /recall-types                                     (settings.appointment_workflow)
 *   PUT    /recall-types/{id}                                 (settings.appointment_workflow)
 *   DELETE /recall-types/{id}                                 (settings.appointment_workflow)
 *   GET    /recalls/worklist?due_from=&due_to=&recall_type_id=&hide_days=&min_overdue_days=&per_page=&page=  (recalls.view)
 *   POST   /recalls/send-reminders  {recall_ids: []}                                                          (recalls.manage)
 *   GET    /leads/{lead}/recalls                            (recalls.view)
 *   PATCH  /patient-recalls/{id}  {date_due_override?, is_disabled?, note?}                                  (recalls.manage)
 *
 * 79-05/79-06 import from here — this module never re-declares an endpoint.
 */

// ── Day-of check-in (85-02) ───────────────────────────────────────────────

export type CheckinStep = 'arrived' | 'seated' | 'dismissed' | 'undo';

/**
 * PATCH /appointments/{id}/checkin {step}. Steps are cumulative (seating a
 * walk-in stamps arrival too), a stamp is never overwritten, `dismissed`
 * completes the appointment, `undo` clears the latest stamp. Always on.
 */
export const checkInAppointment = async (appointmentId: string, step: CheckinStep): Promise<Appointment> => {
  const response = await api.patch<ApiResponse<Appointment>>(`/appointments/${appointmentId}/checkin`, { step });
  return response.data.data;
};

// ── Config-driven appointment categories (form control + calendar filter) ──

let appointmentCategoriesCache: string[] | null = null;

export const getAppointmentCategories = async (
  options: { force?: boolean } = {},
): Promise<string[]> => {
  if (!options.force && appointmentCategoriesCache) {
    return appointmentCategoriesCache;
  }
  const response = await api.get<{ data: string[] }>('/appointment-categories');
  appointmentCategoriesCache = response.data.data;
  return appointmentCategoriesCache;
};

// ── Recall types (settings CRUD) ───────────────────────────────────────────

export interface CreateRecallTypeData {
  name: string;
  interval_months: number;
  interval_days: number;
  trigger_categories: string[];
  reminder_body?: string | null;
  is_active?: boolean;
}

export interface UpdateRecallTypeData extends Partial<CreateRecallTypeData> {}

export const getRecallTypes = async (): Promise<RecallType[]> => {
  const response = await api.get<ApiResponse<RecallType[]>>('/recall-types');
  return response.data.data;
};

export const createRecallType = async (data: CreateRecallTypeData): Promise<RecallType> => {
  const response = await api.post<ApiResponse<RecallType>>('/recall-types', data);
  return response.data.data;
};

export const updateRecallType = async (id: string, data: UpdateRecallTypeData): Promise<RecallType> => {
  const response = await api.put<ApiResponse<RecallType>>(`/recall-types/${id}`, data);
  return response.data.data;
};

export const deleteRecallType = async (id: string): Promise<void> => {
  await api.delete(`/recall-types/${id}`);
};

// ── Recall worklist + batch WhatsApp reminders ──────────────────────────────

export interface RecallWorklistParams {
  due_from?: string;
  due_to?: string;
  recall_type_id?: string;
  hide_days?: number;
  min_overdue_days?: number;
  per_page?: number;
  page?: number;
}

export const getRecallWorklist = async (
  params: RecallWorklistParams = {},
): Promise<PaginatedResponse<PatientRecall>> => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  const response = await api.get<PaginatedResponse<PatientRecall>>(`/recalls/worklist?${query}`);
  return response.data;
};

export const sendRecallReminders = async (
  recallIds: string[],
): Promise<{ queued: number; skipped: number }> => {
  const response = await api.post<ApiResponse<{ queued: number; skipped: number }>>(
    '/recalls/send-reminders',
    { recall_ids: recallIds },
  );
  return response.data.data;
};

// ── Per-patient recall state (lead detail chips) ────────────────────────────

export const getLeadRecalls = async (leadId: string): Promise<PatientRecall[]> => {
  const response = await api.get<ApiResponse<PatientRecall[]>>(`/leads/${leadId}/recalls`);
  return response.data.data;
};

export interface CreateLeadRecallData {
  recall_type_id: string;
  /** Omitted → server derives today + the type's interval. */
  date_due?: string;
  note?: string;
}

/** Quick-recall chips on the lead appointments tab (recalls.manage). Upserts the (lead, type) row. */
export const createLeadRecall = async (
  leadId: string,
  data: CreateLeadRecallData,
): Promise<PatientRecall> => {
  const response = await api.post<ApiResponse<PatientRecall>>(`/leads/${leadId}/recalls`, data);
  return response.data.data;
};

export interface UpdatePatientRecallData {
  date_due_override?: string | null;
  is_disabled?: boolean;
  note?: string | null;
}

export const updatePatientRecall = async (
  id: string,
  data: UpdatePatientRecallData,
): Promise<PatientRecall> => {
  const response = await api.patch<ApiResponse<PatientRecall>>(`/patient-recalls/${id}`, data);
  return response.data.data;
};
